import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ArrowRight, Sparkle, Crown, Tag, BookOpen, Compass, Question } from "@phosphor-icons/react";
import AnnouncementBanner from "@/components/AnnouncementBanner";

const NAV_LINKS = [
  { to: "/big-picture", label: "The Big Picture", Icon: Compass, accent: true, testid: "nav-big-picture-link" },
  { to: "/founders-story", label: "Founder's Story", Icon: BookOpen, testid: "nav-founder-story-link" },
  { to: "/finds/community", label: "Fantastic Finds", Icon: Sparkle, testid: "nav-finds-link" },
  { to: "/faq", label: "FAQ", Icon: Question, testid: "nav-faq-link" },
  { to: "/plans", label: "Pricing", Icon: Tag, testid: "nav-plans-link" },
  { to: "/founding", label: "Founding Council", Icon: Crown, accent: true, testid: "nav-founding-link" },
];

export default function PublicHeader() {
  const { user } = useAuth();
  const navigate = useNavigate();
  return (
    <>
    <AnnouncementBanner />
    <header className="sticky top-0 z-40 border-b border-[#D8D3C8] bg-[#FDFBF7]/85 backdrop-blur">
      <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5 sm:gap-4 shrink-0" data-testid="public-logo-link">
          <img src="/vv_logo.jpg" alt="Vintage Vantage seal" className="h-14 w-14 sm:h-20 sm:w-20 rounded-full object-cover ring-1 ring-[#E5E0D6] shrink-0" />
          <div className="flex flex-col leading-none">
            <p className="font-serif-display font-bold tracking-tight leading-[0.92] text-xl sm:text-4xl">
              <span className="block sm:inline">Vintage</span>{" "}
              <span className="block sm:inline">Vantage</span>
            </p>
            <p className="hidden sm:block label-caps mt-1.5 text-[#B47A3C]" style={{ letterSpacing: "0.16em", fontSize: "10px" }} data-testid="public-tagline">
              The first cross-listing studio for high-end vintage
            </p>
          </div>
        </Link>
        <nav className="flex items-center gap-2 sm:gap-4">
          {NAV_LINKS.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={l.testid}
              className={`hidden lg:inline-flex items-center gap-1.5 text-[15px] font-semibold transition-colors ${l.accent ? "text-[#B47A3C] hover:text-[#0D1B2A]" : "text-[#0D1B2A] hover:text-[#B47A3C]"}`}
            >
              <l.Icon size={17} weight="fill" /> {l.label}
            </Link>
          ))}
          {user ? (
            <button
              data-testid="nav-dashboard-button"
              onClick={() => navigate("/dashboard")}
              className="bg-[#0D1B2A] text-[#FDFBF7] h-10 sm:h-11 px-3 sm:px-5 text-sm rounded-none font-medium inline-flex items-center gap-1.5 sm:gap-2 transition-transform active:scale-95 hover:bg-black"
            >
              Go to dashboard <ArrowRight size={16} weight="bold" />
            </button>
          ) : (
            <>
              <button
                data-testid="nav-signin-button"
                onClick={() => navigate("/login")}
                className="h-10 sm:h-11 px-3 sm:px-4 text-sm rounded-none font-medium border border-[#0D1B2A] hover:bg-[#F5F3ED] transition-colors"
              >
                Sign in
              </button>
              <div className="flex flex-col items-center">
                <button
                  data-testid="nav-getstarted-button"
                  onClick={() => navigate("/register")}
                  className="bg-[#0D1B2A] text-[#FDFBF7] h-10 sm:h-11 px-3 sm:px-5 text-sm rounded-none font-medium inline-flex items-center gap-1.5 sm:gap-2 transition-transform active:scale-95 hover:bg-black"
                >
                  Get started <ArrowRight size={16} weight="bold" />
                </button>
                <p className="hidden sm:block italic text-[10px] text-[#8A867D] mt-1 tracking-wide" data-testid="nav-smarter-caption">list smarter, not harder.</p>
              </div>
            </>
          )}
        </nav>
      </div>
      {/* Second nav row — phones & tablets, so every link is always reachable */}
      <div className="lg:hidden border-t border-[#D8D3C8]/70" data-testid="mobile-nav-row">
        <div className="max-w-6xl mx-auto px-3 py-2.5 flex flex-wrap items-center justify-center gap-x-4 gap-y-1.5">
          {NAV_LINKS.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`${l.testid}-mobile`}
              className={`inline-flex items-center gap-1 text-[13px] font-semibold transition-colors ${l.accent ? "text-[#B47A3C] hover:text-[#0D1B2A]" : "text-[#0D1B2A] hover:text-[#B47A3C]"}`}
            >
              <l.Icon size={14} weight="fill" /> {l.label}
            </Link>
          ))}
        </div>
      </div>
    </header>
    </>
  );
}
